import React from "react";
import { ThunkDispatch } from "redux-thunk";
import { showSnack } from "react-redux-snackbar";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Col,
  Dropdown,
  DropdownToggle,
  Row,
  DropdownItem,
  DropdownMenu,
  Label
} from "reactstrap";
import { connect } from "react-redux";
import { Input } from "@cadmakers-ui/core";
import { Feedback, endpoints } from "../../..";
import { ApiRequest, IAppState, LoadingDisplay } from "../../../../Common";
import { endpoints as workflowEndpoints } from "../../../../Workflows/Constants/endpoints";
import { WorkflowState } from "../../../../Workflows/Models/WorkflowState";
import ResponseHandler from "../../../../App/ResponseHandler";
import styles from "../../../Styles/FeedbackForceTransitionStateModal.module.scss";

type Props = {
  feedback: Feedback;
  isModalOpen: boolean;
  onStateChange?: (feedbackCode: string, newState: WorkflowState) => void;
  toggleModal: () => void;
};

type DispatchProps = {
  showSnack: (uniqueId: any, args: any) => void;
};

type State = {
  dropdownOpen: boolean;
  possibleStatesToTransitionTo: WorkflowState[];
  selectedDropdownState?: WorkflowState;
  loadedStatesForFeedbackCode?: string;
  sendEmail?: boolean;
};

function mapDispatchToProps(dispatch: ThunkDispatch<IAppState, {}, any>): DispatchProps {
  return {
    showSnack: (uniqueId: any, args: any) => dispatch(showSnack(uniqueId, args))
  };
}

class FeedbackForceTransitionStateModal extends React.Component<Props & DispatchProps, State> {
  private overrideLock: boolean;

  constructor(props: Props & DispatchProps) {
    super(props);

    this.state = {
      dropdownOpen: false,
      possibleStatesToTransitionTo: [],
      sendEmail: true
    };

    this.overrideLock = false;

    this.toggleDropdown = this.toggleDropdown.bind(this);
    this.onClickDropdownItem = this.onClickDropdownItem.bind(this);
    this.forceTransitionState = this.forceTransitionState.bind(this);
    this.showPostOverrideSnackbar = this.showPostOverrideSnackbar.bind(this);
    this.showErrorSnackbar = this.showErrorSnackbar.bind(this);
  }

  componentDidUpdate() {
    this.loadPossibleStates();
  }

  onClickDropdownItem(clickedState: WorkflowState) {
    this.setState((prevState: State) => ({
      selectedDropdownState: clickedState
    }));
  }

  toggleDropdown() {
    this.setState((prevState: State) => ({
      dropdownOpen: !prevState.dropdownOpen
    }));
  }

  // loads the possible states this feedback can be force transitioned to, if they are not loaded already
  loadPossibleStates() {
    if (
      this.props.isModalOpen &&
      !!this.props.feedback &&
      this.props.feedback.code !== this.state.loadedStatesForFeedbackCode
    ) {
      ApiRequest.fromPath(
        workflowEndpoints.getStates(this.props.feedback.workflowState.workflowCode)
      )
        .withAuthHeader()
        .get()
        .then(response => {
          ResponseHandler.handle(
            response,
            result => {
              // this was successful
              this.setState((prevState: State) => ({
                ...prevState,
                possibleStatesToTransitionTo: result.payload,
                loadedStatesForFeedbackCode: this.props.feedback.code,
                selectedDropdownState: this.props.feedback.workflowState
              }));
            },
            result => {},
            () => {}
          );
        });
    }
  }

  forceTransitionState() {
    if (!this.state.selectedDropdownState) {
      return;
    }

    if (!this.claimOverrideLock) {
      return;
    }

    // POST transition to server
    const targetState = this.state.selectedDropdownState;
    const { onStateChange } = this.props;
    const releaseLock = this.releaseOverrideLock.bind(this);

    const body = {
      StateId: targetState.id,
      sendEmail: this.state.sendEmail
    };

    ApiRequest.fromPath(endpoints.feedback.forceState(this.props.feedback.code))
      .withJson(body)
      .withAuthHeader()
      .post()
      .then(response => {
        if (response.status === 200) {
          if (onStateChange) {
            onStateChange(this.props.feedback.code, targetState);
          }
          this.showPostOverrideSnackbar(targetState);
          this.props.toggleModal();
        } else {
          response.json().then(json => {
            this.showErrorSnackbar(response.status, json.title, targetState);
          });
        }
        releaseLock();
      });
  }

  showPostOverrideSnackbar(newState: WorkflowState) {
    this.props.showSnack("FORCE_FEEDBACK_STATE_TRANSITION", {
      label: `Successfully changed the state to "${newState.name.toString()}"`,
      timeout: 2000
    });
  }

  showErrorSnackbar(errorCode: number, errorDescription: string, state: WorkflowState) {
    this.props.showSnack("FORCE_FEEDBACK_STATE_TRANSITION", {
      label: `Failed to change the feedback's state to ${state.name.toString()}: ${errorDescription} (Error Code ${errorCode})`,
      timeout: 5000
    });
  }

  claimOverrideLock() {
    if (!this.overrideLock) {
      this.overrideLock = true;
      return true;
    }
    return false;
  }

  releaseOverrideLock() {
    this.overrideLock = false;
  }

  render() {
    return (
      <span>
        <Modal isOpen={this.props.isModalOpen} toggle={this.props.toggleModal}>
          <ModalHeader toggle={this.props.toggleModal}>Feedback State Override</ModalHeader>
          <ModalBody>
            <p>
              State overrides should only be used when a normal state transition is not available.
              Users will still receive notifications as if the state was transitioned normally.
            </p>
            <FormGroup>
              <Row>
                <Col sm={3}>
                  <Label>Target State:</Label>
                </Col>
                <Col sm={9}>
                  {this.state.loadedStatesForFeedbackCode === this.props.feedback.code && (
                    <Dropdown
                      className={styles.stateDropdown}
                      isOpen={this.state.dropdownOpen}
                      toggle={this.toggleDropdown}
                    >
                      <DropdownToggle className={styles.stateDropdownToggle} caret>
                        {!!this.state.selectedDropdownState &&
                          this.state.selectedDropdownState.name.toString()}
                      </DropdownToggle>
                      <DropdownMenu>
                        {this.state.possibleStatesToTransitionTo.map(el => {
                          return (
                            <DropdownItem
                              key={el.id}
                              active={
                                !!this.state.selectedDropdownState &&
                                el.id === this.state.selectedDropdownState.id
                              }
                              onClick={() => {
                                this.onClickDropdownItem(el);
                              }}
                            >
                              {el.name}
                            </DropdownItem>
                          );
                        })}
                      </DropdownMenu>
                    </Dropdown>
                  )}
                  {this.state.loadedStatesForFeedbackCode !== this.props.feedback.code && (
                    <LoadingDisplay />
                  )}
                </Col>
              </Row>
              <Row>
                <Col sm={3} />
                <Col sm={9}>
                  <div style={{ display: "flex", marginTop: "16px" }}>
                    <Input
                      checked={this.state.sendEmail}
                      onChange={() => {
                        this.setState(prev => {
                          return { ...prev, sendEmail: !prev.sendEmail };
                        });
                      }}
                      type="checkbox"
                      translate={undefined}
                    />
                    <span style={{ marginLeft: "16px" }}>Send email to subscribed users</span>
                  </div>
                </Col>
              </Row>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={this.forceTransitionState}>
              Save
            </Button>{" "}
            <Button color="secondary" onClick={this.props.toggleModal}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      </span>
    );
  }
}

export default connect(null, mapDispatchToProps)(FeedbackForceTransitionStateModal);
